import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Card, Table, Tag, Typography, Row, Col, Statistic,
    Avatar, Button, Alert, Spin, Timeline, Space, Dropdown, message,
    Modal, Segmented, Input, Divider
} from 'antd';
import {
    ThunderboltOutlined, ArrowLeftOutlined,
    TeamOutlined, CrownOutlined, UserOutlined,
    CheckCircleOutlined, ClockCircleOutlined, CalendarOutlined,
    CarOutlined, DollarOutlined, WalletOutlined,
    DownloadOutlined, FileExcelOutlined, ReloadOutlined,
    EditOutlined, ExclamationCircleOutlined
} from '@ant-design/icons';
import affiliatesService from '../../common/services/affiliates.service';
import dayjs from 'dayjs';
import XLSX from 'xlsx-js-style';

const { Title, Text, Paragraph } = Typography;

const RevenuesBonusRechargeDetailPage = () => {
    const { type, beneficiaryId } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [exporting, setExporting] = useState(false);

    // Payment modal states
    const [paymentModal, setPaymentModal] = useState({ visible: false, bonus: null });
    const [paymentMode, setPaymentMode] = useState('wave');
    const [paymentRef, setPaymentRef] = useState('');
    const [sendingWavePayout, setSendingWavePayout] = useState(false);
    const [savingPayment, setSavingPayment] = useState(false);

    const fetchDetail = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await affiliatesService.getSameDayRechargeBonusDetail(type, beneficiaryId);
            if (response.success) {
                setData(response.data);
            } else {
                setError(response.message || 'Erreur lors du chargement');
            }
        } catch (err) {
            console.error('Erreur:', err);
            setError('Erreur lors du chargement des details');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDetail();
    }, [type, beneficiaryId]);

    // Open payment modal for a bonus
    const handlePaymentClick = (bonus) => {
        setPaymentModal({ visible: true, bonus });
        setPaymentMode('wave');
        setPaymentRef('');
    };

    // Wave payout handler
    const handleWavePayout = async () => {
        const bonus = paymentModal.bonus;
        if (!bonus?.commissionId) {
            message.error('Impossible de payer: commission non trouvée');
            return;
        }

        const amountToPay = bonus.bonusAmount || 2000;

        Modal.confirm({
            title: 'Confirmer le paiement Wave',
            icon: <ExclamationCircleOutlined />,
            content: (
                <div>
                    <p>Vous allez envoyer <strong>{amountToPay.toLocaleString()} F</strong> via Wave à :</p>
                    <p><strong>{data?.beneficiary?.firstName} {data?.beneficiary?.lastName}</strong></p>
                    <p style={{ color: '#8c8c8c' }}>{data?.beneficiary?.phone}</p>
                    <p style={{ marginTop: 12, color: '#faad14' }}>
                        Cette action est irréversible. L'argent sera débité de votre compte Wave Business.
                    </p>
                </div>
            ),
            okText: 'Confirmer et payer',
            cancelText: 'Annuler',
            okButtonProps: { style: { background: '#1890ff' } },
            onOk: async () => {
                setSendingWavePayout(true);
                try {
                    const response = await affiliatesService.sendBonusRechargeWavePayout({
                        commissionId: bonus.commissionId,
                        amount: amountToPay
                    });

                    if (response.success) {
                        message.success(`Paiement Wave effectué avec succès ! Référence: ${response.data.transactionId}`);
                        setPaymentModal({ visible: false, bonus: null });
                        setPaymentRef('');
                        fetchDetail();
                    } else {
                        message.error(response.message || 'Erreur lors du paiement Wave');
                    }
                } catch (error) {
                    console.error('Erreur Wave Payout:', error);
                    message.error(error.response?.data?.message || 'Erreur lors du paiement Wave');
                } finally {
                    setSendingWavePayout(false);
                }
            }
        });
    };

    // Manual payment handler
    const handleSavePayment = async () => {
        const bonus = paymentModal.bonus;
        if (!bonus?.commissionId) {
            message.error('Impossible de sauvegarder: commission non trouvée');
            return;
        }

        setSavingPayment(true);
        try {
            const response = await affiliatesService.paySameDayRechargeBonus(bonus.commissionId, {
                paymentStatus: 'paid',
                paymentReference: paymentRef,
                paymentMethod: 'manual'
            });

            if (response.success) {
                message.success('Paiement enregistré avec succès');
                setPaymentModal({ visible: false, bonus: null });
                setPaymentRef('');
                fetchDetail();
            } else {
                message.error(response.message || 'Erreur lors de la sauvegarde');
            }
        } catch (error) {
            console.error('Erreur sauvegarde paiement:', error);
            message.error('Erreur lors de la sauvegarde du paiement');
        } finally {
            setSavingPayment(false);
        }
    };

    const getStatusTag = (status) => {
        const statusConfig = {
            pending: { color: 'orange', label: 'En attente', icon: <ClockCircleOutlined /> },
            pending_validation: { color: 'blue', label: 'Validation', icon: <ClockCircleOutlined /> },
            approved: { color: 'green', label: 'Approuve', icon: <CheckCircleOutlined /> },
            paid: { color: 'cyan', label: 'Paye', icon: <CheckCircleOutlined /> },
            cancelled: { color: 'red', label: 'Annule', icon: null }
        };
        const config = statusConfig[status] || { color: 'default', label: status };
        return <Tag color={config.color} icon={config.icon}>{config.label}</Tag>;
    };

    const getPaymentMethodTag = (method) => {
        if (method === 'wave') {
            return <Tag color="blue">Wave</Tag>;
        } else if (method === 'orange_money') {
            return <Tag color="orange">Orange Money</Tag>;
        }
        return <Tag>Inconnu</Tag>;
    };

    // Fonction pour préparer les données d'export
    const prepareExportData = () => {
        if (!data?.bonusesByDate) return [];

        const exportData = [];
        data.bonusesByDate.forEach(dayData => {
            (dayData.qualifyingDrivers || []).forEach(driver => {
                exportData.push({
                    'Date': dayjs(dayData.date).format('DD/MM/YYYY'),
                    'Chauffeur': `${driver.firstName || ''} ${driver.lastName || ''}`.trim(),
                    'Téléphone': driver.phone || '',
                    'Montant recharge (F)': driver.rechargeAmount || 0,
                    'Moyen paiement': driver.paymentMethod === 'wave' ? 'Wave' : driver.paymentMethod === 'orange_money' ? 'Orange Money' : 'Autre',
                    'Heure': driver.rechargeTime ? dayjs(driver.rechargeTime).format('HH:mm') : '',
                    'Bonus (F)': dayData.bonusAmount || 2000,
                    'Statut': dayData.status === 'paid' ? 'Payé' : dayData.status === 'approved' ? 'Approuvé' : 'En attente'
                });
            });
        });

        return exportData;
    };

    // Export CSV
    const exportToCSV = () => {
        setExporting(true);
        try {
            const exportData = prepareExportData();
            if (exportData.length === 0) {
                message.warning('Aucune donnée à exporter');
                return;
            }

            const benefName = `${data?.beneficiary?.firstName || ''} ${data?.beneficiary?.lastName || ''}`.trim();
            const benefType = type === 'ambassador' ? 'AMBASSADEUR' : 'AFFILIÉ';
            const titleLine = `BONUS RECHARGE JOUR DE ${benefName.toUpperCase()} (${benefType})`;
            const periodLine = `EXPORTÉ LE: ${dayjs().format('DD/MM/YYYY à HH:mm')}`;

            const headers = Object.keys(exportData[0]);
            const csvContent = [
                titleLine,
                periodLine,
                '',
                headers.join(';'),
                ...exportData.map(row => headers.map(h => `"${row[h]}"`).join(';'))
            ].join('\n');

            const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            const benefNameFile = benefName.replace(/\s+/g, '_');
            const filename = `bonus_recharge_${benefNameFile}_${dayjs().format('YYYY-MM-DD_HHmm')}.csv`;

            link.setAttribute('href', url);
            link.setAttribute('download', filename);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            message.success(`Export CSV réussi: ${filename}`);
        } catch (error) {
            console.error('Erreur export CSV:', error);
            message.error('Erreur lors de l\'export CSV');
        } finally {
            setExporting(false);
        }
    };

    // Export Excel (XLS)
    const exportToExcel = () => {
        setExporting(true);
        try {
            const exportData = prepareExportData();
            if (exportData.length === 0) {
                message.warning('Aucune donnée à exporter');
                return;
            }

            const benefName = `${data?.beneficiary?.firstName || ''} ${data?.beneficiary?.lastName || ''}`.trim();
            const benefType = type === 'ambassador' ? 'AMBASSADEUR' : 'AFFILIÉ';

            // Styles
            const titleStyle = {
                font: { bold: true, sz: 14, color: { rgb: "000000" } },
                alignment: { horizontal: "left", vertical: "center" }
            };
            const periodStyle = {
                font: { bold: true, sz: 11, color: { rgb: "555555" } },
                alignment: { horizontal: "left", vertical: "center" }
            };
            const headerStyle = {
                font: { bold: true, sz: 11, color: { rgb: "FFFFFF" } },
                fill: { fgColor: { rgb: "FAAD14" } },
                alignment: { horizontal: "center", vertical: "center" },
                border: {
                    top: { style: "thin", color: { rgb: "000000" } },
                    bottom: { style: "thin", color: { rgb: "000000" } },
                    left: { style: "thin", color: { rgb: "000000" } },
                    right: { style: "thin", color: { rgb: "000000" } }
                }
            };
            const cellStyle = {
                border: {
                    top: { style: "thin", color: { rgb: "CCCCCC" } },
                    bottom: { style: "thin", color: { rgb: "CCCCCC" } },
                    left: { style: "thin", color: { rgb: "CCCCCC" } },
                    right: { style: "thin", color: { rgb: "CCCCCC" } }
                },
                alignment: { vertical: "center" }
            };

            const titleText = `BONUS RECHARGE JOUR DE ${benefName.toUpperCase()} (${benefType})`;
            const periodText = `EXPORTÉ LE: ${dayjs().format('DD/MM/YYYY à HH:mm')}`;

            const headerRows = [
                [{ v: titleText, s: titleStyle }],
                [{ v: periodText, s: periodStyle }],
                []
            ];

            const worksheet = XLSX.utils.aoa_to_sheet(headerRows);

            const headers = Object.keys(exportData[0]);
            const dataWithHeaders = [
                headers.map(h => ({ v: h.toUpperCase(), s: headerStyle })),
                ...exportData.map(row => headers.map(h => ({ v: row[h], s: cellStyle })))
            ];

            XLSX.utils.sheet_add_aoa(worksheet, dataWithHeaders, { origin: 'A4' });

            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Bonus Recharge');

            const colWidths = headers.map(key => ({
                wch: Math.max(key.length, ...exportData.map(row => String(row[key] || '').length), 15) + 2
            }));
            worksheet['!cols'] = colWidths;

            const colCount = headers.length;
            worksheet['!merges'] = [
                { s: { r: 0, c: 0 }, e: { r: 0, c: colCount - 1 } },
                { s: { r: 1, c: 0 }, e: { r: 1, c: colCount - 1 } }
            ];

            worksheet['!rows'] = [
                { hpt: 25 },
                { hpt: 20 },
                { hpt: 15 },
                { hpt: 22 }
            ];

            const benefNameFile = benefName.replace(/\s+/g, '_');
            const filename = `bonus_recharge_${benefNameFile}_${dayjs().format('YYYY-MM-DD_HHmm')}.xlsx`;
            XLSX.writeFile(workbook, filename);

            message.success(`Export Excel réussi: ${filename}`);
        } catch (error) {
            console.error('Erreur export Excel:', error);
            message.error('Erreur lors de l\'export Excel');
        } finally {
            setExporting(false);
        }
    };

    // Menu d'export
    const exportMenuItems = [
        {
            key: 'csv',
            label: 'Exporter en CSV',
            icon: <DownloadOutlined />,
            onClick: exportToCSV
        },
        {
            key: 'excel',
            label: 'Exporter en Excel',
            icon: <FileExcelOutlined />,
            onClick: exportToExcel
        }
    ];

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                <Spin size="large" />
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ padding: 24 }}>
                <Button
                    icon={<ArrowLeftOutlined />}
                    onClick={() => navigate('/dashboard/revenues-bonus-recharge')}
                    style={{ marginBottom: 16 }}
                >
                    Retour
                </Button>
                <Alert type="error" message={error} showIcon />
            </div>
        );
    }

    const { beneficiary, bonusesByDate, stats } = data;
    const isAmbassador = type === 'ambassador';

    return (
        <div className="bonus-recharge-detail-page">
            {/* Header */}
            <div style={{ marginBottom: 24 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
                    <Button
                        icon={<ArrowLeftOutlined />}
                        onClick={() => navigate('/dashboard/revenues-bonus-recharge')}
                    >
                        Retour à la liste
                    </Button>
                    <Space>
                        <Dropdown menu={{ items: exportMenuItems }} placement="bottomRight">
                            <Button icon={<DownloadOutlined />} loading={exporting}>
                                Exporter
                            </Button>
                        </Dropdown>
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={fetchDetail}
                            loading={loading}
                        >
                            Actualiser
                        </Button>
                    </Space>
                </div>

                <Card>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
                        <Avatar
                            size={70}
                            icon={isAmbassador ? <CrownOutlined /> : <TeamOutlined />}
                            style={{
                                backgroundColor: isAmbassador ? '#722ed1' : '#1890ff',
                                fontSize: 32
                            }}
                        />
                        <div style={{ flex: 1 }}>
                            <Title level={3} style={{ margin: 0 }}>
                                {beneficiary.firstName} {beneficiary.lastName}
                            </Title>
                            <Text type="secondary" style={{ fontSize: 16 }}>{beneficiary.phone}</Text>
                            <div style={{ marginTop: 8 }}>
                                <Tag
                                    color={isAmbassador ? 'purple' : 'blue'}
                                    icon={isAmbassador ? <CrownOutlined /> : <TeamOutlined />}
                                    style={{ fontSize: 14, padding: '4px 12px' }}
                                >
                                    {isAmbassador ? 'Ambassadeur' : 'Affilie'}
                                </Tag>
                            </div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                            <div style={{ fontSize: 14, color: '#888' }}>Total gagne</div>
                            <div style={{ fontSize: 28, fontWeight: 'bold', color: '#3f8600' }}>
                                {(stats.totalAmount || 0).toLocaleString()} F
                            </div>
                        </div>
                    </div>
                </Card>
            </div>

            {/* Explication */}
            <Alert
                type="info"
                icon={<ThunderboltOutlined style={{ color: '#faad14' }} />}
                message={
                    <span style={{ fontWeight: 600 }}>
                        Comment fonctionne le bonus recharge jour ?
                    </span>
                }
                description={
                    <Paragraph style={{ margin: 0, marginTop: 8 }}>
                        Vous recevez <strong style={{ color: '#faad14' }}>2 000 F</strong> lorsque <strong>au moins 2 de vos NOUVEAUX chauffeurs inscrits le meme jour</strong>{' '}
                        effectuent chacun une recharge d'au moins <strong>2 000 F</strong> le jour de leur inscription.
                        <span style={{ display: 'block', marginTop: 8, color: '#1890ff' }}>
                            <strong>Important:</strong> Seuls les chauffeurs inscrits ET recharges le MEME JOUR comptent pour ce bonus.
                        </span>
                        {isAmbassador && (
                            <span style={{ display: 'block', marginTop: 8, color: '#722ed1' }}>
                                <strong>Note Ambassadeur:</strong> Seuls vos chauffeurs recrutes DIRECTEMENT comptent pour ce bonus
                                (les chauffeurs recrutes par vos affilies ne comptent pas).
                            </span>
                        )}
                    </Paragraph>
                }
                style={{ marginBottom: 24 }}
            />

            {/* Statistiques */}
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col xs={24} sm={8}>
                    <Card>
                        <Statistic
                            title={<span><ThunderboltOutlined style={{ marginRight: 8 }} />Total bonus</span>}
                            value={stats.totalBonuses}
                            valueStyle={{ color: '#faad14' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={8}>
                    <Card>
                        <Statistic
                            title={<span><CarOutlined style={{ marginRight: 8 }} />Chauffeurs concernes</span>}
                            value={stats.uniqueDrivers}
                            valueStyle={{ color: '#1890ff' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={8}>
                    <Card>
                        <Statistic
                            title={<span><CalendarOutlined style={{ marginRight: 8 }} />Jours avec bonus</span>}
                            value={stats.daysWithBonus}
                            valueStyle={{ color: '#722ed1' }}
                        />
                    </Card>
                </Col>
            </Row>

            {/* Historique par date */}
            <Card
                title={
                    <span>
                        <CalendarOutlined style={{ marginRight: 8 }} />
                        Historique des bonus par date
                    </span>
                }
            >
                {bonusesByDate.length === 0 ? (
                    <Alert type="info" message="Aucun bonus enregistre" showIcon />
                ) : (
                    <Timeline mode="left">
                        {bonusesByDate.map((dayData, index) => (
                            <Timeline.Item
                                key={dayData.date}
                                color={index === 0 ? 'green' : 'blue'}
                                label={
                                    <div style={{ fontWeight: 600, color: '#333' }}>
                                        {dayjs(dayData.date).format('DD MMMM YYYY')}
                                    </div>
                                }
                            >
                                <Card
                                    size="small"
                                    style={{
                                        backgroundColor: '#fafafa',
                                        borderLeft: '3px solid #faad14'
                                    }}
                                >
                                    <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                                        <Tag color="gold" style={{ fontSize: 14, padding: '4px 12px' }}>
                                            <ThunderboltOutlined style={{ marginRight: 6 }} />
                                            Bonus = {(dayData.bonusAmount || 2000).toLocaleString()} F
                                        </Tag>
                                        <Space>
                                            {getStatusTag(dayData.status)}
                                            {dayData.status !== 'paid' && dayData.commissionId && (
                                                <Button
                                                    type="primary"
                                                    size="small"
                                                    icon={<DollarOutlined />}
                                                    onClick={() => handlePaymentClick(dayData)}
                                                    style={{ background: '#52c41a', borderColor: '#52c41a' }}
                                                >
                                                    Payer
                                                </Button>
                                            )}
                                            {dayData.status === 'paid' && dayData.paymentReference && (
                                                <Tag color="green" style={{ fontSize: 11 }}>
                                                    Ref: {dayData.paymentReference}
                                                </Tag>
                                            )}
                                        </Space>
                                    </div>

                                    <Paragraph style={{ margin: 0, marginBottom: 12, color: '#666' }}>
                                        <strong>Vous recevez {(dayData.bonusAmount || 2000).toLocaleString()} F</strong> car <strong>{dayData.driversCount} nouveaux chauffeurs{isAmbassador ? ' directs' : ''}</strong> inscrits ce jour ont effectue une recharge d'au moins 2 000 F :
                                    </Paragraph>

                                    <Table
                                        dataSource={dayData.qualifyingDrivers}
                                        rowKey={(record) => `${record._id}-${record.rechargeTime}`}
                                        pagination={false}
                                        size="small"
                                        columns={[
                                            {
                                                title: 'Chauffeur',
                                                key: 'driver',
                                                render: (_, record) => (
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                        <Avatar
                                                            size={32}
                                                            icon={<UserOutlined />}
                                                            style={{ backgroundColor: '#52c41a' }}
                                                        />
                                                        <div>
                                                            <div style={{ fontWeight: 500 }}>
                                                                {record.firstName} {record.lastName}
                                                            </div>
                                                            <div style={{ fontSize: 11, color: '#888' }}>
                                                                {record.phone}
                                                            </div>
                                                        </div>
                                                    </div>
                                                )
                                            },
                                            {
                                                title: 'Montant recharge',
                                                key: 'rechargeAmount',
                                                width: 140,
                                                render: (_, record) => (
                                                    <div>
                                                        <span style={{ color: '#1890ff', fontWeight: 600, fontSize: 15 }}>
                                                            <WalletOutlined style={{ marginRight: 6 }} />
                                                            {(record.rechargeAmount || 0).toLocaleString()} F
                                                        </span>
                                                    </div>
                                                )
                                            },
                                            {
                                                title: 'Moyen',
                                                key: 'paymentMethod',
                                                width: 110,
                                                render: (_, record) => getPaymentMethodTag(record.paymentMethod)
                                            },
                                            {
                                                title: 'Heure',
                                                key: 'time',
                                                width: 80,
                                                render: (_, record) => (
                                                    <span style={{ color: '#888', fontSize: 12 }}>
                                                        {record.rechargeTime ? dayjs(record.rechargeTime).format('HH:mm') : '-'}
                                                    </span>
                                                )
                                            }
                                        ]}
                                    />
                                </Card>
                            </Timeline.Item>
                        ))}
                    </Timeline>
                )}
            </Card>

            {/* Resume des paiements */}
            <Card
                title={
                    <span>
                        <DollarOutlined style={{ marginRight: 8 }} />
                        Resume des paiements
                    </span>
                }
                style={{ marginTop: 24 }}
            >
                <Row gutter={[24, 16]}>
                    <Col xs={24} sm={8}>
                        <div style={{ textAlign: 'center', padding: 16, backgroundColor: '#f6ffed', borderRadius: 8 }}>
                            <CheckCircleOutlined style={{ fontSize: 32, color: '#52c41a', marginBottom: 8 }} />
                            <div style={{ fontSize: 12, color: '#888' }}>Deja paye</div>
                            <div style={{ fontSize: 24, fontWeight: 'bold', color: '#52c41a' }}>
                                {(stats.paidAmount || 0).toLocaleString()} F
                            </div>
                        </div>
                    </Col>
                    <Col xs={24} sm={8}>
                        <div style={{ textAlign: 'center', padding: 16, backgroundColor: '#fffbe6', borderRadius: 8 }}>
                            <ClockCircleOutlined style={{ fontSize: 32, color: '#faad14', marginBottom: 8 }} />
                            <div style={{ fontSize: 12, color: '#888' }}>En attente</div>
                            <div style={{ fontSize: 24, fontWeight: 'bold', color: '#faad14' }}>
                                {(stats.pendingAmount || 0).toLocaleString()} F
                            </div>
                        </div>
                    </Col>
                    <Col xs={24} sm={8}>
                        <div style={{ textAlign: 'center', padding: 16, backgroundColor: '#f0f5ff', borderRadius: 8 }}>
                            <ThunderboltOutlined style={{ fontSize: 32, color: '#1890ff', marginBottom: 8 }} />
                            <div style={{ fontSize: 12, color: '#888' }}>Total</div>
                            <div style={{ fontSize: 24, fontWeight: 'bold', color: '#1890ff' }}>
                                {(stats.totalAmount || 0).toLocaleString()} F
                            </div>
                        </div>
                    </Col>
                </Row>
            </Card>

            {/* Modal de paiement */}
            <Modal
                title={
                    <span>
                        <DollarOutlined style={{ marginRight: 8 }} />
                        Enregistrer un paiement
                    </span>
                }
                open={paymentModal.visible}
                onCancel={() => {
                    setPaymentModal({ visible: false, bonus: null });
                    setPaymentRef('');
                }}
                footer={paymentMode === 'manual' ? [
                    <Button key="cancel" onClick={() => {
                        setPaymentModal({ visible: false, bonus: null });
                        setPaymentRef('');
                    }}>
                        Annuler
                    </Button>,
                    <Button
                        key="save"
                        type="primary"
                        loading={savingPayment}
                        onClick={handleSavePayment}
                        style={{ background: '#52c41a', borderColor: '#52c41a' }}
                    >
                        Enregistrer le paiement
                    </Button>
                ] : null}
                width={500}
            >
                {paymentModal.bonus && (
                    <>
                        {/* Infos du bonus */}
                        <div style={{
                            background: '#fafafa',
                            borderRadius: 8,
                            padding: 16,
                            marginBottom: 16
                        }}>
                            <Row gutter={16}>
                                <Col span={8}>
                                    <div style={{ fontSize: 12, color: '#888' }}>Date</div>
                                    <div style={{ fontWeight: 600 }}>
                                        {dayjs(paymentModal.bonus.date).format('DD/MM/YYYY')}
                                    </div>
                                </Col>
                                <Col span={8}>
                                    <div style={{ fontSize: 12, color: '#888' }}>Montant</div>
                                    <div style={{ fontWeight: 600, color: '#faad14' }}>
                                        {(paymentModal.bonus.bonusAmount || 2000).toLocaleString()} F
                                    </div>
                                </Col>
                                <Col span={8}>
                                    <div style={{ fontSize: 12, color: '#888' }}>Statut</div>
                                    {getStatusTag(paymentModal.bonus.status)}
                                </Col>
                            </Row>
                            <div style={{ marginTop: 8, fontSize: 12, color: '#888' }}>
                                {paymentModal.bonus.driversCount} chauffeur(s) qualifié(s)
                            </div>
                        </div>

                        <Divider style={{ margin: '12px 0' }} />

                        {/* Sélecteur de mode de paiement */}
                        <div style={{ marginBottom: 20 }}>
                            <Segmented
                                value={paymentMode}
                                onChange={(value) => {
                                    setPaymentMode(value);
                                    setPaymentRef('');
                                }}
                                block
                                size="large"
                                options={[
                                    {
                                        value: 'wave',
                                        label: (
                                            <div style={{ padding: '4px 0' }}>
                                                <WalletOutlined style={{ marginRight: 6 }} />
                                                Payer via Wave
                                            </div>
                                        )
                                    },
                                    {
                                        value: 'manual',
                                        label: (
                                            <div style={{ padding: '4px 0' }}>
                                                <EditOutlined style={{ marginRight: 6 }} />
                                                Saisie manuelle
                                            </div>
                                        )
                                    }
                                ]}
                            />
                        </div>

                        {/* MODE WAVE */}
                        {paymentMode === 'wave' && (
                            <div style={{
                                background: '#f0f5ff',
                                border: '1px solid #adc6ff',
                                borderRadius: 10,
                                padding: 20
                            }}>
                                <div style={{ marginBottom: 16, textAlign: 'center' }}>
                                    <div style={{ fontSize: 13, color: '#595959' }}>
                                        Le paiement de <strong>{(paymentModal.bonus.bonusAmount || 2000).toLocaleString()} F</strong> sera envoyé à :
                                    </div>
                                    <div style={{ fontWeight: 600, fontSize: 16, marginTop: 4 }}>
                                        {beneficiary?.firstName} {beneficiary?.lastName}
                                    </div>
                                    <div style={{ color: '#8c8c8c' }}>{beneficiary?.phone}</div>
                                </div>

                                <Button
                                    type="primary"
                                    size="large"
                                    block
                                    icon={<WalletOutlined />}
                                    loading={sendingWavePayout}
                                    onClick={handleWavePayout}
                                    style={{
                                        background: 'linear-gradient(135deg, #1a73e8 0%, #4285f4 100%)',
                                        border: 'none',
                                        height: 48,
                                        fontWeight: 600,
                                        borderRadius: 8
                                    }}
                                >
                                    Envoyer {(paymentModal.bonus.bonusAmount || 2000).toLocaleString()} F via Wave
                                </Button>
                                <div style={{ marginTop: 8, fontSize: 11, color: '#5b8c00', textAlign: 'center' }}>
                                    Le paiement sera envoyé directement sur Wave et la référence sera automatiquement enregistrée
                                </div>
                            </div>
                        )}

                        {/* MODE MANUEL */}
                        {paymentMode === 'manual' && (
                            <div style={{
                                background: '#fffbe6',
                                border: '1px solid #ffe58f',
                                borderRadius: 10,
                                padding: 20
                            }}>
                                <div style={{ marginBottom: 16 }}>
                                    <div style={{ marginBottom: 6, fontWeight: 500 }}>Référence de paiement</div>
                                    <Input
                                        placeholder="Ex: TX-2025021001234"
                                        value={paymentRef}
                                        onChange={(e) => setPaymentRef(e.target.value)}
                                        size="large"
                                        prefix={<WalletOutlined style={{ color: '#bfbfbf' }} />}
                                    />
                                    <div style={{ marginTop: 6, fontSize: 11, color: '#8c8c8c' }}>
                                        Entrez la référence si vous avez déjà effectué le paiement
                                    </div>
                                </div>
                                <div style={{ textAlign: 'center', fontSize: 13, color: '#595959' }}>
                                    Montant : <strong style={{ color: '#faad14' }}>{(paymentModal.bonus.bonusAmount || 2000).toLocaleString()} F</strong>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </Modal>
        </div>
    );
};

export default RevenuesBonusRechargeDetailPage;
